import {Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CommonModule} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {OrganizationService} from '../../../services/organization.service';
import {OrganizationMembershipService} from '../../../services/organization-membership.service';
import {ToastService} from '../../../services/toast.service';
import {OrganizationModel, OrganizationRole, UpdateOrganization} from '../../../models/organization.model';
import {OrganizationMembershipModel} from '../../../models/organization-membership.model';
import {ButtonComponent} from '../../shared/button/button.component';
import {PageComponent} from '../../shared/page/page.component';
import {DialogComponent} from '../../shared/dialog/dialog.component';
import {EditableTextComponent, EditableTextSaveResult} from '../../shared/editable-text/editable-text.component';
import {FormAndControls} from '../../shared/utils';
import {SpinnerComponent} from '../../spinner/spinner.component';
import {PageTabDirective} from '../../shared/page/page-tab.directive';
import {NavigateService} from '../../../services/navigate.service';

interface OrgState {
  loading?: boolean;
  error?: string;
}

interface OrgFormDef {
  name: string;
  description: string;
}

/**
 * Organization management page for admins.
 *
 * Allows organization administrators to:
 * - View and edit organization details (name, description)
 * - Manage members (add, remove, change roles)
 * - Delete the organization
 *
 * Access restricted to organization admins only.
 */
@Component({
  selector: 'app-organization-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    PageComponent,
    DialogComponent,
    EditableTextComponent,
    SpinnerComponent,
    FormsModule,
    PageTabDirective
  ],
  templateUrl: './organization-management.component.html',
  host: {
    class: 'app-container'
  }
})
export class OrganizationManagementComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly navigate = inject(NavigateService);
  protected readonly orgService = inject(OrganizationService);
  private readonly membershipService = inject(OrganizationMembershipService);
  protected readonly toastService = inject(ToastService);

  protected readonly organization = signal<OrganizationModel | null>(null);
  protected readonly members = signal<OrganizationMembershipModel[]>([]);
  protected readonly state = signal<OrgState>({});

  // Add a member form
  protected readonly showAddMemberDialog = signal(false);
  protected readonly newMemberEmail = signal('');
  protected readonly newMemberRole = signal<OrganizationRole>(OrganizationRole.Reader);
  protected readonly isAddingMember = signal(false);

  // Expose OrganizationRole enum to template
  readonly OrganizationRole = OrganizationRole;

  // Edit mode based on permissions
  protected readonly editMode = computed(() => {
    const org = this.organization();
    return org?.canManageOrg ? 'manual' : 'never';
  });

  protected form = this.createForm({
    name: '',
    description: ''
  });

  constructor() {
    effect(() => {
      const org = this.organization();
      if (org) {
        this.form = this.createForm({
          name: org.name,
          description: org.description || ''
        });
      }
    });
  }

  private createForm(org: OrgFormDef) {
    return new FormAndControls<OrgFormDef>({
      name: new FormControl(org.name, {validators: [Validators.required], nonNullable: true}),
      description: new FormControl(org.description, {nonNullable: true})
    });
  }

  ngOnInit() {
    const orgId = this.route.snapshot.paramMap.get('id');
    if (!orgId) {
      this.navigate.profile();
      return;
    }

    this.loadOrganization(orgId);
  }

  protected updateState(state: Partial<OrgState>): void {
    this.state.update(prevState => ({...prevState, ...state}));
  }

  protected loadOrganization(id: string) {
    this.updateState({loading: true, error: undefined});

    this.toastService.handleResult(
      'Load Organization',
      this.orgService.get(id),
      {showSuccess: false}
    ).subscribe({
      next: (org) => {
        if (!org.canManageOrg) {
          this.toastService.error('Permission Denied', 'You do not have permission to manage this organization');
          this.navigate.profile();
          return;
        }
        this.organization.set(org);
        this.loadMembers(id);
      },
      error: (_err) => {
        this.navigate.profile();
      },
      complete: () => {
        this.updateState({loading: false});
      }
    });
  }

  protected loadMembers(orgId: string) {
    this.toastService.handleResult(
      'Load members',
      this.membershipService.list(orgId),
      {showSuccess: false}
    ).subscribe((result) => {
      this.members.set(result.items);
    });
  }

  saveOrganization(field: keyof UpdateOrganization, result: EditableTextSaveResult): void {
    const org = this.organization();
    if (!org) return;

    const request: UpdateOrganization = {id: org.id};
    (request as any)[field] = result.formControl.value;

    const op = this.orgService.update(request);
    result.success = op;

    this.toastService.handleResult('Update Organization', op, (updatedOrg) => {
      this.organization.set(updatedOrg);
    });
  }

  protected openAddMemberDialog() {
    this.newMemberEmail.set('');
    this.newMemberRole.set(OrganizationRole.Reader);
    this.showAddMemberDialog.set(true);
  }

  protected closeAddMemberDialog() {
    this.showAddMemberDialog.set(false);
  }

  protected addMember() {
    const org = this.organization();
    if (!org) return;

    const email = this.newMemberEmail().trim();
    if (!email) {
      this.toastService.error('Invalid Input', 'Email address is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.toastService.error('Invalid Input', 'Please enter a valid email address');
      return;
    }

    this.isAddingMember.set(true);
    this.toastService.handleResult(
      'Add Member',
      this.membershipService.create(org.id, {
        email: email,
        role: this.newMemberRole()
      }),
      (membership) => {
        this.members.update(m => [...m, membership]);
        this.closeAddMemberDialog();
      }
    ).subscribe({
      complete: () => {
        this.isAddingMember.set(false);
      }
    });
  }

  protected updateMemberRole(membership: OrganizationMembershipModel, newRole: OrganizationRole) {
    if (membership.role === newRole) return;

    this.toastService.handleResult(
      'Update Role',
      this.membershipService.update({
        id: membership.id,
        role: newRole
      }),
      (updated) => {
        this.members.update(members =>
          members.map(m => m.id === updated.id ? updated : m)
        );
      }
    );
  }

  protected removeMember(membership: OrganizationMembershipModel) {
    const org = this.organization();
    if (!org) return;

    if (!confirm(`Remove ${membership.name} from ${org.name}?`)) {
      return;
    }

    this.toastService.handleResult(
      'Remove Member',
      this.membershipService.delete(membership.id),
      () => {
        this.members.update(members => members.filter(m => m.id !== membership.id));
      }
    );
  }

  protected deleteOrganization() {
    const org = this.organization();
    if (!org) return;

    if (!confirm(`Delete organization "${org.name}"? This cannot be undone.`)) {
      return;
    }

    this.toastService.handleResult(
      'Delete Organization',
      this.orgService.delete(org.id),
      () => {
        this.navigate.profile();
      }
    );
  }
}
